import {
    MAX_PAGE_SIZE_FOR_COMPARISON,
    STATISTICS,
} from '@client/app/shared-modules/statistics/statistics.constant';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import type { MonTableCollection } from '@client/ng2/models/table-collection.interface';
import type {
    SiteAnalyticsDataRequest,
    SiteAnalyticsDataResult,
    SiteAnalyticsLinkCell,
    SiteAnalyticsScaffoldConfig,
    SiteAnalyticsTableRow,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import type {
    SiteAnalyticsPageDefinition,
    SiteAnalyticsPageDefinitionDeps,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-definition.type';

interface SiteAnalyticsPageLike {
    url?: string | null;
    title?: string | null;
    label?: string | null;
    visits?: string | number | null;
}

const PAGES_WITHOUT_VISITS_CONFIG: SiteAnalyticsScaffoldConfig = {
    pageHeaderKey: 'siteAnalytics.pagesWithoutVisits.header',
    pageSubheaderKey: 'siteAnalytics.pagesWithoutVisits.subheader',
    tableKey: 'site-analytics-pages-without-visits',
    dataSource: 'pagesWithoutVisits',
    exportCategory: 'pages_without_visits',
    exportVariants: [
        {
            id: 'all',
            labelKey: 'siteAnalytics.pagesWithoutVisits.export.all',
            categoryRefSegment: 'all',
        },
        {
            id: 'html',
            labelKey: 'siteAnalytics.pagesWithoutVisits.export.html',
            categoryRefSegment: 'html',
        },
        {
            id: 'document',
            labelKey: 'siteAnalytics.pagesWithoutVisits.export.document',
            categoryRefSegment: 'document',
        },
    ],
    searchPlaceholderKey: 'siteAnalytics.scaffold.search.placeholder',
    emptyMessageKey: 'siteAnalytics.scaffold.table.emptyMessage',
    emptySearchMessageKey: 'siteAnalytics.scaffold.table.emptySearchMessage',
    tableCaptionKey: 'siteAnalytics.pagesWithoutVisits.table.caption',
    exportButtonLabelKey: 'siteAnalytics.scaffold.export.buttonLabel',
    dateRangeLabelKey: 'siteAnalytics.scaffold.dateRange.label',
    compareToggleLabelKey: 'siteAnalytics.scaffold.compare.toggleLabel',
    compareDateRangeLabelKey: 'siteAnalytics.scaffold.compare.dateRangeLabel',
    defaultSortBy: 'url',
    defaultSortDir: 'asc',
    comparisonMaxPageSize: MAX_PAGE_SIZE_FOR_COMPARISON,
    chart: {
        titleKey: 'siteAnalytics.pagesWithoutVisits.chart.title',
        primarySeriesLabelKey: 'siteAnalytics.scaffold.chart.series.currentVisits',
        comparisonSeriesLabelKey: 'siteAnalytics.scaffold.chart.series.comparisonVisits',
    },
    showChart: false,
    columns: [
        {
            id: 'page',
            headerKey: 'siteAnalytics.scaffold.table.column.url',
            displayType: 'link',
            width: 'xl',
            ariaLabelKey: 'siteAnalytics.scaffold.table.urlAriaLabel{{url}}',
        },
        {
            id: 'visits',
            headerKey: 'siteAnalytics.scaffold.table.column.visits',
            displayType: 'number',
            width: 'md',
        },
        {
            id: 'visitsComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.visitsComparison',
            displayType: 'number',
            visibility: 'comparison',
            width: 'md',
        },
    ],
};

const loadPagesWithoutVisitsData = async (
    request: SiteAnalyticsDataRequest,
    deps: SiteAnalyticsPageDefinitionDeps,
): Promise<SiteAnalyticsDataResult> => {
    const { statsRepoService, statisticsParamsService } = deps;
    const isComparisonMode = Boolean(request.comparisonDateRange);

    const currentParams = statisticsParamsService.buildListParams({
        limit: request.perPage,
        page: request.page,
        domainId: request.domainId,
        dateRange: request.dateRange,
        dateFormat: STATISTICS.DATE_FORMAT,
        sortBy: request.sortBy,
        sortDirection: request.sortDir,
        search: request.search,
    });

    const currentPagesResponse = await statsRepoService.getPagesWithoutVisits(
        currentParams,
        contextTokens.NO_PARSE_SEARCH,
    );

    if (!isComparisonMode || !request.comparisonDateRange) {
        const normalizedCurrentRows = normalizeRows(currentPagesResponse);

        return {
            rows: normalizedCurrentRows,
            chart: {
                labels: normalizedCurrentRows.map((row) => getPageLabel(row)),
                primaryValues: normalizedCurrentRows.map((row) => toInteger(row.visits)),
            },
            summary: null,
            total: getTotal(currentPagesResponse, normalizedCurrentRows.length),
            perPage: getPerPage(currentPagesResponse, request.perPage),
            page: getCurrentPage(currentPagesResponse, request.page),
        };
    }

    const urls = currentPagesResponse
        .map((page) => String(page.url || ''))
        .filter((url) => Boolean(url));

    const comparePagesResponse = urls.length
        ? await statsRepoService.getPagesByUrl({
            domainId: request.domainId,
            from: request.comparisonDateRange.startDate.format(STATISTICS.DATE_FORMAT),
            to: request.comparisonDateRange.endDate.format(STATISTICS.DATE_FORMAT),
            urls,
        })
        : [];

    const normalizedComparisonRows = normalizeRows(currentPagesResponse, comparePagesResponse);

    return {
        rows: normalizedComparisonRows,
        chart: {
            labels: normalizedComparisonRows.map((row) => getPageLabel(row)),
            primaryValues: normalizedComparisonRows.map((row) => toInteger(row.visits)),
            comparisonValues: normalizedComparisonRows.map((row) => toInteger(row.visitsComparison)),
        },
        summary: null,
        total: getTotal(currentPagesResponse, normalizedComparisonRows.length),
        perPage: getPerPage(currentPagesResponse, request.perPage),
        page: getCurrentPage(currentPagesResponse, request.page),
    };
};

function normalizeRows (
    currentPages: MonTableCollection<SiteAnalyticsPageLike>,
    comparisonPages?: SiteAnalyticsPageLike[],
): SiteAnalyticsTableRow[] {
    const comparisonByUrl = new Map<string, SiteAnalyticsPageLike>();

    if (comparisonPages) {
        for (const page of comparisonPages) {
            const urlKey = String(page.url || '');
            if (!comparisonByUrl.has(urlKey)) {
                comparisonByUrl.set(urlKey, page);
            }
        }
    }

    return currentPages.map((page, index) => {
        const url = String(page.url || '');
        const comparisonPage = comparisonByUrl.get(url);
        const title = String(page.title || '');

        return {
            id: `${url || 'row'}-${index}`,
            page: {
                label: url,
                href: url,
                ...(title && title !== url ? { tooltip: title } : {}),
            },
            visits: toInteger(page.visits),
            visitsComparison: comparisonPage ? toInteger(comparisonPage.visits) : null,
        };
    });
}

function getPageLabel (row: SiteAnalyticsTableRow): string {
    const linkCell = toLinkCell(row.page);
    if (linkCell) {
        return getUrlPath(linkCell.label);
    }

    if (typeof row.page === 'string') {
        return getUrlPath(row.page);
    }

    return '';
}

function getUrlPath (value: string): string {
    try {
        const url = new URL(value);

        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return value;
    }
}

function toLinkCell (value: unknown): SiteAnalyticsLinkCell | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const link = value as { label?: unknown; href?: unknown; tooltip?: unknown };
    if (typeof link.label !== 'string' || typeof link.href !== 'string') {
        return null;
    }

    return {
        label: link.label,
        href: link.href,
        ...(typeof link.tooltip === 'string' && link.tooltip ? { tooltip: link.tooltip } : {}),
    };
}

function toInteger (value: unknown): number {
    const parsed = Number.parseInt(String(value ?? 0), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function getTotal (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'total' in collection) {
        const total = Number((collection as { total?: unknown }).total);
        if (!Number.isNaN(total) && total > 0) {
            return total;
        }
    }

    return fallback;
}

function getPerPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'perPage' in collection) {
        const perPage = Number((collection as { perPage?: unknown }).perPage);
        if (!Number.isNaN(perPage) && perPage > 0) {
            return perPage;
        }
    }

    return fallback;
}

function getCurrentPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'currentPage' in collection) {
        const currentPage = Number((collection as { currentPage?: unknown }).currentPage);
        if (!Number.isNaN(currentPage) && currentPage > 0) {
            return currentPage;
        }
    }

    return fallback;
}

export const PAGES_WITHOUT_VISITS_DEFINITION: SiteAnalyticsPageDefinition = {
    key: 'pagesWithoutVisits',
    config: PAGES_WITHOUT_VISITS_CONFIG,
    loadData: loadPagesWithoutVisitsData,
};

export const PAGES_WITHOUT_VISITS_PAGE_CONFIG = PAGES_WITHOUT_VISITS_DEFINITION.config;
